import { h, van } from "../lib/tb.js";
import * as api from "../lib/api.js";
import { fileExt } from "../lib/util.js";
import { Card, Button, Badge, Thumb, CopyUrlLink, CopyButton, Table } from "../lib/components.js";
import { toast } from "../lib/toast.js";

const { div } = van.tags;

/** One salon row. `onChanged` is called after a successful upload. */
function SalonRow(salon, onChanged) {
  const busy = van.state(false);
  const msg = van.state(null); // { kind, text, url? }
  // Google My Maps photos can't be embedded directly (CORS), so proxy them.
  const initialPreview = salon.imageUrl
    ? salon.imageUrl
    : (salon.gxMediaUrl ? api.proxyImageUrl(salon.gxMediaUrl) : "");
  const preview = van.state(initialPreview);

  const runUpload = async (fn) => {
    busy.val = true;
    msg.val = null;
    try {
      const r = await fn();
      preview.val = r.url + "?t=" + Date.now();
      msg.val = { kind: "ok", url: r.url };
    } catch(err) {
      msg.val = { kind: "error", text: err.message };
      toast.error(`Nahrání selhalo: ${err.message}`);
    } finally {
      busy.val = false;
    }
  };

  // Overwrite the existing file when the salon already has one, else name it
  // after the slug (the extension comes from the uploaded/downloaded image).
  const uploadFile = (file) => runUpload(() =>
    api.uploadImage(salon.hasImage ? salon.imageFile : `${salon.slug}.${fileExt(file)}`, file));
  const useGoogleImage = () => runUpload(() =>
    api.useGoogleImage({ url: salon.gxMediaUrl, filename: salon.imageFile, slug: salon.slug }));

  const input = h("input", {
    type: "file", accept: "image/*", class: "hidden",
    onchange: (e) => {
      const file = e.target.files[0];
      if(file) uploadFile(file);
      e.target.value = "";
    },
  });

  const action = div({ class: "space-y-2" },
    input,
    div({ class: "flex flex-wrap items-center gap-2" },
      Button({ variant: "ghost", size: "sm", disabled: busy, onclick: () => input.click() },
        () => busy.val ? "Nahrávám…" : (salon.hasImage ? "Nahradit obrázek" : "Nahrát obrázek")),
      salon.gxMediaUrl
        ? Button({ variant: "ghost", size: "sm", disabled: busy, onclick: useGoogleImage },
            "Použít obrázek z Google Maps")
        : "",
    ),
    // After a change the file lives on the CDN; when the salon has no image_url
    // yet it still needs to be linked in Google My Maps.
    () => msg.val?.kind === "ok"
      ? (salon.hasImage
          ? h("span.text-xs.text-emerald-600", "Nahrazeno ✓")
          : div({ class: "space-y-1" },
              h("p.text-xs.text-slate-500", "Vložte tuto URL do pole image_url v Google My Maps:"),
              CopyUrlLink(msg.val.url),
            ))
      : msg.val?.kind === "error"
        ? h("span.text-xs.text-red-600", msg.val.text)
        : "",
  );

  const statusBadge = salon.hasImage
    ? (salon.matched ? Badge("green", "Přiřazeno") : Badge("amber", "Chybí soubor"))
    : Badge("red", "Bez obrázku");

  return h("tr", { class: salon.hasImage ? "" : "bg-amber-50/60" }, [
    h("td", () => Thumb(preview.val)),
    h("td", [
      h("div.font-medium.text-slate-900", salon.name),
      h("div.text-xs.text-slate-500", [salon.city].filter(Boolean).join(" · ")),
      statusBadge,
    ]),
    h("td", salon.imageUrl ? CopyUrlLink(salon.imageUrl) : h("span.text-slate-400", "—")),
    h("td", action),
  ]);
}

export function ImagesSection() {
  const state = van.state("idle"); // idle | loading | ready | error
  const error = van.state("");
  const data = van.state(null);

  const load = async () => {
    state.val = "loading";
    error.val = "";
    try {
      data.val = await api.getImages();
      console.log(data.val);
      state.val = "ready";
    } catch(err) {
      error.val = err.message;
      state.val = "error";
      toast.error(`Načtení obrázků selhalo: ${err.message}`);
    }
  };

  load();

  const salonTable = () => {
    const d = data.val;
    if(!d) return "";
    return Table(
      ["Náhled", "Salon", "URL obrázku", "Akce"],
      d.salons.map((s) => SalonRow(s, load)),
    );
  };

  const unassignedTable = () => {
    const d = data.val;
    if(!d) return "";
    if(!d.unassigned.length)
      return h("p.text-sm.text-slate-500", "Žádné nepřiřazené obrázky.");
    return Table(
      ["Náhled", "URL obrázku"],
      d.unassigned.map((f) => h("tr", [
        h("td", Thumb(f.url)),
        h("td", [
          h("p.text-xs.text-slate-500.mb-1", "Vložte tuto URL do pole image_url v Google My Maps:"),
          CopyUrlLink(f.url)
        ]),
      ])),
    );
  };

  const reloadBtn = () => Button(
    { variant: "secondary", onclick: load, disabled: () => state.val === "loading" },
    () => state.val === "loading" ? "Načítám…" : (data.val ? "Obnovit" : "Načíst obrázky"),
  );

  return div(
    Card({
      title: "Nepřiřazené obrázky",
      description: "Zobrazuje obrázky, které jsou nahrané na SFTP, ale nepoužívají se u žádného salonu.",
    },
      h(".card__body", [
        h(".flex.justify-end", reloadBtn()),
        unassignedTable,
      ]),
    ),
    Card({
      title: "Obrázky salonů",
      description: "Stáhne seznam salonů z Google My Maps a zobrazí seznam přiřazených obrázků. Umožňuje nahrávat a aktualizovat obrázky k salonům. Změny z Google My Maps se neprojeví okamžitě, je tam cca minutová prodleva.",
    },
      h(".card__body", [
        h(".flex.justify-end", reloadBtn()),
        salonTable,
      ]),
    ),
  );
}
